import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  type ConditionCheckResponseInput,
  type InspectionConditionCheck,
} from '@/services/inspection.service';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';

/**
 * Group 4 — "4. AS-RECEIVED / AS-FOUND CONDITION" on the inspection detail page.
 *
 * The parameter list is FROZEN: it was copied from the equipment model's
 * configuration when this inspection was created and does not change afterwards,
 * however the model is later reconfigured. Only the three answer fields are
 * editable, which is why the label is rendered as plain text and there is no add,
 * delete or reorder control anywhere in this component.
 *
 * An inspection created before Group 4 has no checklist. It returns an empty list
 * and this section renders nothing at all — opening such an inspection must never
 * bring a checklist into existence (Owner decision D3).
 */

interface RowDraft {
  asFoundCondition: string;
  status: 'OK' | 'NOT_OK' | null;
  remarks: string;
}

const toDraft = (c: InspectionConditionCheck): RowDraft => ({
  asFoundCondition: c.asFoundCondition ?? '',
  status: c.status,
  remarks: c.remarks ?? '',
});

export function ConditionChecksSection({
  inspectionId,
  canEdit,
}: {
  inspectionId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['inspection-condition-checks', inspectionId],
    queryFn: () => inspectionService.getConditionChecks(inspectionId),
  });

  const checks = data ?? [];

  useEffect(() => {
    if (!data) return;
    const next: Record<string, RowDraft> = {};
    for (const c of data) next[c.id] = toDraft(c);
    setDrafts(next);
    setDirty(false);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const responses: ConditionCheckResponseInput[] = checks.map((c) => {
        const d = drafts[c.id] ?? toDraft(c);
        return {
          id: c.id,
          asFoundCondition: d.asFoundCondition,
          remarks: d.remarks,
          ...(d.status ? { status: d.status } : {}),
        };
      });
      return inspectionService.saveConditionChecks(inspectionId, responses);
    },
    onSuccess: () => {
      toast.success('As-found condition saved');
      setDirty(false);
      void queryClient.invalidateQueries({
        queryKey: ['inspection-condition-checks', inspectionId],
      });
    },
    onError: () => toast.error('Could not save the as-found condition'),
  });

  const update = (id: string, patch: Partial<RowDraft>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id]!, ...patch } }));
    setDirty(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading…
      </div>
    );
  }

  // No checklist — an inspection created before this feature. Render nothing and,
  // critically, ask the server for nothing further.
  if (checks.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">Parameter / Check</th>
              <th className="px-3 py-2 font-medium">As-Found Condition</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c) => {
              const d = drafts[c.id] ?? toDraft(c);
              return (
                <tr key={c.id} className="border-b border-border last:border-0 align-top">
                  {/* Read-only: the snapshot label is the historical record. */}
                  <td className="px-3 py-2 font-medium text-foreground">{c.parameterLabel}</td>
                  <td className="px-3 py-2">
                    <Input
                      value={d.asFoundCondition}
                      disabled={!canEdit}
                      aria-label={`As-found condition for ${c.parameterLabel}`}
                      onChange={(e) => update(c.id, { asFoundCondition: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      {(['OK', 'NOT_OK'] as const).map((value) => (
                        <label key={value} className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name={`status-${c.id}`}
                            value={value}
                            disabled={!canEdit}
                            checked={d.status === value}
                            aria-label={`${value === 'OK' ? 'OK' : 'Not OK'} for ${c.parameterLabel}`}
                            onChange={() => update(c.id, { status: value })}
                          />
                          {value === 'OK' ? 'OK' : 'Not OK'}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={d.remarks}
                      disabled={!canEdit}
                      aria-label={`Remarks for ${c.parameterLabel}`}
                      onChange={(e) => update(c.id, { remarks: e.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />}
            Save As-Found Condition
          </Button>
        </div>
      )}
    </div>
  );
}
